import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import { ScrollView, View } from 'react-native';

import { ProviderCard } from '@/components/discovery/provider-card';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { SeoHead } from '@/components/seo-head';
import { ThemedText } from '@/components/themed-text';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSearchProviders } from '@/hooks/use-discovery';
import { PROFILE_NOT_FOUND_TITLE } from '@/lib/seo/profile-meta';
import { useRegionStore } from '@/store/regionStore';

const SUGGESTIONS = 6;

/**
 * Perfil público (@slug) que não existe ou não está publicado. Em vez de um
 * beco sem saída, mostra perfis parecidos com o nome digitado e, sem nenhum
 * parecido, os da região do visitante.
 */
export function ProfileNotFound({ slug }: { slug: string }) {
  const region = useRegionStore((s) => s.region);
  const term = slug.replace(/[-_.]+/g, ' ').trim();

  const similar = useSearchProviders(region, { q: term });
  const similarCards = similar.data?.pages[0]?.items ?? [];
  const noSimilar = !similar.isLoading && similarCards.length === 0;

  const nearby = useSearchProviders(region, {});
  const nearbyCards = nearby.data?.pages[0]?.items ?? [];

  const cards = (noSimilar ? nearbyCards : similarCards).slice(0, SUGGESTIONS);
  const loading = similar.isLoading || (noSimilar && nearby.isLoading);

  return (
    <View className="flex-1 bg-neutral-50">
      {/* Mesmo título que a função da Vercel põe no 404 do perfil. */}
      <SeoHead title={PROFILE_NOT_FOUND_TITLE} noindex />
      <PublicBrandHeader />
      <ScrollView keyboardShouldPersistTaps="handled" contentContainerClassName="flex-grow">
        <View className="mx-auto w-full max-w-4xl gap-8 px-4 py-10">
          <View className="items-center gap-4">
            <View
              className="h-20 w-20 items-center justify-center rounded-full bg-primary-50 dark:bg-primary"
              style={{ borderCurve: 'continuous' }}
            >
              <Ionicons
                className="text-primary dark:text-white"
                name="storefront-outline"
                size={34}
              />
            </View>
            <View className="items-center gap-2">
              <ThemedText role="heading" aria-level={1} className="text-center font-bold text-2xl">
                Não encontramos essa página
              </ThemedText>
              <ThemedText className="max-w-md text-center text-neutral-500">
                Talvez o link tenha sido digitado errado ou o profissional mudou o endereço. Veja se
                quem você procura está aqui:
              </ThemedText>
            </View>
          </View>

          <View className="gap-3">
            <ThemedText className="font-semibold text-neutral-700">
              {noSimilar ? 'Outros profissionais perto de você' : 'Você quis dizer?'}
            </ThemedText>
            <View className="flex-row flex-wrap gap-3">
              {loading
                ? Array.from({ length: 3 }, (_, i) => (
                    <Skeleton
                      key={i}
                      className="h-56 flex-1 rounded-2xl"
                      style={{ minWidth: 160 }}
                    />
                  ))
                : cards.map((card) => (
                    <View
                      key={card.providerId}
                      className="flex-1"
                      style={{ minWidth: 160, maxWidth: 300 }}
                    >
                      <ProviderCard
                        card={card}
                        onPress={() => router.push(`/@${card.slug}` as never)}
                      />
                    </View>
                  ))}
            </View>
            {!loading && cards.length === 0 ? (
              <ThemedText className="text-center text-neutral-500">
                Nenhum perfil para sugerir agora.
              </ThemedText>
            ) : null}
          </View>

          <View className="items-center gap-2">
            <Button variant="default" onPress={() => router.push('/buscar' as never)}>
              Procurar outro profissional
            </Button>
            <Button variant="ghost" onPress={() => router.replace('/')}>
              Ir para o início
            </Button>
          </View>
        </View>
        <PublicBrandFooter />
      </ScrollView>
    </View>
  );
}
